// ─────────────────────────────────────────────────────────────────────────────
// OrionContext — o estado que a aplicação inteira compartilha.
//
// Na versão 1.x essas informações eram variáveis soltas no topo do renderer.js
// (operatorName, currentTheme, conclusionMsg…) que qualquer função podia
// alterar de qualquer lugar. Aqui elas viram um estado só, com funções
// nomeadas para mudá-lo — que é o que torna possível saber quem mudou o quê.
//
// O que mora aqui: sessão do operador, tema, tela ativa, avisos (toasts),
// versão do app e o aviso de atualização. O que NÃO mora aqui: qualquer coisa
// que interesse a uma tela só — isso fica na própria tela.
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { config as configPlataforma, versaoApp, atualizacao, ehDesktop, janela, segredo } from '../services/plataforma';
import { autenticar, registrarAcesso, registrarLogoutNaSaida, carregarMapaOperadores, codigoDoOperador } from '../services/authApi';
import { assinarIncidentes } from '../services/incidentesApi';
import { assumirTicketAoAtender } from '../services/mysuiteApi';
import { analisarColeta, registrarPulo, registrarMesmoClienteIgnorado } from '../services/puloFilaApi';
import { liberaFormalizacao } from '../services/assumirTicket';
import { configurarSupabase, credenciaisEmUso } from '../services/supabase';
import { useAtividade } from '../services/useAtividade';
import { useFilaRetorno } from '../services/useFilaRetorno';
import { useRetornoSistemas } from '../services/useRetornoSistemas';
import { useFilasMysuite } from '../services/useFilasMysuite';
import { useNotificacoes } from '../services/useNotificacoes';
import { notificar } from '../services/notificacaoSistema';
import {
  carregarPerfil, assinarOperador, permissoesDoSetor, viewInicial, ehSetorSuporte,
} from '../services/permissoes';
import { MENSAGEM_CONCLUSAO_PADRAO, GEMINI_MODELO_PADRAO } from '../config/ambiente';

const OrionContext = createContext(null);

/** Acesso ao estado compartilhado. Use dentro de qualquer tela do Orion. */
export function useOrion() {
  const ctx = useContext(OrionContext);
  if (!ctx) throw new Error('useOrion precisa estar dentro de <OrionProvider>.');
  return ctx;
}

// Dois temas, só. Os translúcidos que existiam foram retirados: davam quatro
// variações para manter e nenhuma delas era o padrão da casa.
export const TEMAS = ['dark', 'light'];

/**
 * De quanto em quanto tempo o cadastro do operador é relido.
 *
 * É a rede de segurança do realtime, não o caminho principal — por isso pode ser
 * espaçado. Uma linha de uma tabela de algumas dezenas é consulta barata, mas
 * mudança de cadastro é rara e não justifica insistência.
 */
const INTERVALO_PERFIL_MS = 5 * 60 * 1000;

/** Qualquer valor herdado que não seja um dos dois cai no escuro. */
function temaValido(tema) {
  return TEMAS.includes(tema) ? tema : 'dark';
}

export function OrionProvider({ children }) {
  // ── Sessão ────────────────────────────────────────────────────────────────
  const [operador, setOperador] = useState(null);     // { nome, email, codigo }
  const [carregando, setCarregando] = useState(true); // enquanto a config não chega
  const [erroLogin, setErroLogin] = useState('');
  const [autenticando, setAutenticando] = useState(false);

  // ── Preferências ──────────────────────────────────────────────────────────
  const [tema, setTema] = useState('dark');
  const [mensagemConclusao, setMensagemConclusao] = useState(MENSAGEM_CONCLUSAO_PADRAO);
  const [modeloGemini, setModeloGemini] = useState(GEMINI_MODELO_PADRAO);
  // `senha` é a senha em claro e vive SÓ em memória, para pré-preencher o
  // campo de login. O que vai para o disco é `senhaCifrada` (ver o cofre em
  // services/plataforma.js). Na web `senhaCifrada` fica sempre vazia — lá não
  // há como guardar senha com segurança, então não se guarda.
  const [credenciais, setCredenciais] = useState({
    lembrar: false, email: '', senha: '', senhaCifrada: '',
  });

  // ── Navegação ─────────────────────────────────────────────────────────────
  const [view, setView] = useState('helpdesk');
  // Ticket que a aba Incidentes deve abrir já no formulário de registro.
  // Usado quando a formalização termina em 'Setor de sistemas'/'Suporte
  // avançado' e o operador aceita registrar o incidente: o número viaja
  // junto, e ele não digita de novo o que o Orion já sabe.
  const [ticketParaIncidente, setTicketParaIncidente] = useState(null);
  // O texto formalizado do atendimento em curso, para o Ctrl+E copiar de
  // qualquer lugar do app — inclusive com o foco fora do campo.
  const [textoParaCopiar, setTextoParaCopiar] = useState('');
  // Entrada do histórico que a Formalização deve recarregar (o 'Reutilizar').
  const [formalizacaoParaReusar, setFormalizacaoParaReusar] = useState(null);
  // Ticket que a Formalização deve buscar assim que abrir. Usado pelo card e
  // pelo aviso da fila de retorno: o operador clica em "Atender" e cai no
  // atendimento pronto, sem copiar o número de uma tela para a outra.
  const [ticketParaAbrir, setTicketParaAbrir] = useState(null);
  // Incidente que a aba Incidentes deve abrir assim que entrar, pelo NÚMERO DO
  // TICKET. Sai do resumo do ticket: um ticket no Setor de Sistemas quase
  // sempre tem incidente, e ler o incidente é o passo seguinte natural.
  const [incidenteParaAbrir, setIncidenteParaAbrir] = useState(null);
  const [sidebarRecolhida, setSidebarRecolhida] = useState(false);

  // ── Avisos ────────────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const proximoToastId = useRef(1);

  // ── App ───────────────────────────────────────────────────────────────────
  const [versao, setVersao] = useState('');
  const [updatePronto, setUpdatePronto] = useState(false);
  const [estadoUpdate, setEstadoUpdate] = useState(null);

  // ── Estado da janela (desktop) ────────────────────────────────────────────
  // No Electron o app nasce como botão flutuante; no navegador já nasce aberto,
  // porque lá não existe "recolher para um disco no canto da tela".
  const [modoJanela, setModoJanela] = useState(ehDesktop ? 'button' : 'expanded');
  const [maximizado, setMaximizado] = useState(false);

  // O login precisa das credenciais salvas, que só chegam depois da config.
  // Esta ref evita que o efeito de auto-login rode antes disso.
  const configRecebida = useRef(false);

  // ── Toasts ────────────────────────────────────────────────────────────────

  const fecharToast = useCallback((id) => {
    setToasts(atual => atual.filter(t => t.id !== id));
  }, []);

  /**
   * Mostra um aviso passageiro.
   * `tipo`: 'info' | 'ok' | 'erro'. `duracao` em ms (0 = não fecha sozinho).
   */
  const avisar = useCallback((texto, { tipo = 'info', duracao = 4000, titulo = '', aoClicar = null } = {}) => {
    const id = proximoToastId.current++;
    setToasts(atual => [...atual, { id, texto, tipo, titulo, aoClicar }]);
    if (duracao > 0) setTimeout(() => fecharToast(id), duracao);
    return id;
  }, [fecharToast]);

  // ── Diálogos da coleta (pulo de fila, mesmo cliente) ──────────────────────
  //
  // `assumirAoAtender` PERGUNTA e espera a resposta no meio do fluxo. A
  // pergunta é uma promessa: o diálogo (components/DialogoColeta.js, no shell)
  // a resolve com o que o analista escolheu — ou `null` quando ele desiste.
  const [dialogoColeta, setDialogoColeta] = useState(null);
  const resolverColeta = useRef(null);

  const perguntarColeta = useCallback((dados) => new Promise((resolver) => {
    // Uma pergunta em aberto que é substituída vale como "não".
    resolverColeta.current?.(null);
    resolverColeta.current = resolver;
    setDialogoColeta({ ...dados, agora: new Date() });
  }), []);

  const responderColeta = useCallback((resposta) => {
    const resolver = resolverColeta.current;
    resolverColeta.current = null;
    setDialogoColeta(null);
    resolver?.(resposta);
  }, []);

  // ── Persistência da configuração ──────────────────────────────────────────

  // Monta o objeto que vai para o disco (desktop) ou localStorage (web).
  const montarConfig = useCallback((extras = {}) => ({
    conclusionMsg: mensagemConclusao,
    theme: tema,
    aiModel: modeloGemini,
    operatorName: operador?.nome || '',
    operatorEmail: operador?.email || '',
    rememberCredentials: credenciais.lembrar,
    savedEmail: credenciais.email,
    // Cifrada, nunca em claro. Ver o cofre em services/plataforma.js.
    savedPassword: credenciais.senhaCifrada,
    ...extras,
  }), [mensagemConclusao, tema, modeloGemini, operador, credenciais]);

  const salvarConfig = useCallback((extras = {}) => {
    configPlataforma.salvar(montarConfig(extras));
  }, [montarConfig]);

  // ── Login ─────────────────────────────────────────────────────────────────

  /** Aplica a sessão: guarda o operador e carrega o que depende do código dele. */
  const aplicarSessao = useCallback(async (nome, email) => {
    // O mapa de operadores tem que estar pronto ANTES do que depende do código
    // (lista de atendimentos, controle de atividade e o payload dos tickets).
    await carregarMapaOperadores();
    const codigo = codigoDoOperador(email);
    // O setor decide o que este operador enxerga — o Orion deixou de ser só do
    // suporte. Vem junto da sessão, e não sob demanda, porque a navegação
    // inteira depende dele: carregar depois faria as abas do suporte piscarem
    // na tela de quem não pode vê-las.
    //
    // Daqui para a frente ele é REVALIDADO enquanto a sessão vive (realtime,
    // relógio e volta do foco) — ver o efeito "Cadastro do operador", abaixo.
    const perfil = await carregarPerfil(email);
    // O código da mesma linha vence o do mapa: quem foi cadastrado agora ainda
    // não está no mapa, que só é montado na entrada.
    const sessao = {
      nome, email, codigo: perfil.codigo ?? codigo, setor: perfil.setor,
      // Gerente do suporte (orion.operador.ope_admin). Vem junto da sessão pelo
      // mesmo motivo do setor: é ele que decide a tela inicial, e descobri-lo
      // depois faria o gerente abrir na Formalização e ser jogado no painel um
      // instante mais tarde.
      admin: perfil.admin,
    };
    setOperador(sessao);
    return sessao;
  }, []);

  const entrar = useCallback(async (email, senha, lembrar) => {
    setAutenticando(true);
    setErroLogin('');
    try {
      const res = await autenticar(email, senha);
      if (!res.ok) { setErroLogin(res.mensagem); return false; }

      // ANTES de aplicarSessao, que já consulta orion.operador: se a chave
      // entrasse depois, a primeira consulta da sessão sairia com a chave
      // antiga (ou nenhuma) e o operador entraria sem código.
      if (res.supabase) configurarSupabase(res.supabase);

      const sessao = await aplicarSessao(res.nome, res.email);

      // A senha só é cifrada (e só chega ao disco) se o operador pediu para
      // lembrar. No desktop, quem cifra é o cofre do sistema; na web, `guardar`
      // devolve '' de propósito — lá o "lembrar" vale só para o e-mail.
      const senhaCifrada = lembrar ? await segredo.guardar(senha) : '';

      const novasCredenciais = {
        lembrar: !!lembrar,
        email: lembrar ? email : '',
        senha: lembrar ? senha : '',
        senhaCifrada,
      };
      setCredenciais(novasCredenciais);

      configPlataforma.salvar({
        conclusionMsg: mensagemConclusao,
        theme: tema,
        aiModel: modeloGemini,
        operatorName: sessao.nome,
        operatorEmail: sessao.email,
        rememberCredentials: novasCredenciais.lembrar,
        savedEmail: novasCredenciais.email,
        savedPassword: senhaCifrada,
        // A chave viaja com a sessão porque o Orion ENTRA DIRETO quando há
        // sessão salva, sem passar pelo login — e nesse caminho ela não teria
        // de onde vir. Guardada, ela continua trocável em um lugar só: o
        // próximo login traz a nova e sobrescreve esta.
        supabaseUrl: res.supabase?.url || '',
        supabaseChave: res.supabase?.chave || '',
      });

      registrarAcesso('login', sessao);
      return true;
    } finally {
      setAutenticando(false);
    }
  }, [aplicarSessao, mensagemConclusao, tema, modeloGemini]);

  const sair = useCallback(() => {
    const anterior = operador;
    setOperador(null);
    setView('helpdesk');
    if (anterior) registrarAcesso('logout', anterior);
    // Zera a sessão no disco, mas preserva as credenciais salvas: "Trocar
    // operador" não deve apagar o que o operador pediu para lembrar.
    configPlataforma.salvar({
      conclusionMsg: mensagemConclusao,
      theme: tema,
      aiModel: modeloGemini,
      operatorName: '',
      operatorEmail: '',
      rememberCredentials: credenciais.lembrar,
      savedEmail: credenciais.email,
      savedPassword: credenciais.senhaCifrada,
      // A chave permanece: ela é do aplicativo, não do operador. Apagá-la aqui
      // deixaria a tela de login sem banco — e é justamente ela que precisa
      // consultar o cadastro para o próximo operador entrar.
      supabaseUrl: credenciaisEmUso().url,
      supabaseChave: credenciaisEmUso().chave,
    });
  }, [operador, mensagemConclusao, tema, modeloGemini, credenciais]);

  // ── Tema ──────────────────────────────────────────────────────────────────

  const trocarTema = useCallback((novo) => {
    const t = temaValido(novo);
    setTema(t);
    salvarConfig({ theme: t });
  }, [salvarConfig]);

  // ── Carga inicial ─────────────────────────────────────────────────────────

  useEffect(() => {
    let vivo = true;

    // Aplica a configuração venha ela de onde vier (IPC no desktop,
    // localStorage na web).
    const aplicarConfig = async (cfg) => {
      if (!vivo || !cfg) { setCarregando(false); return; }
      configRecebida.current = true;

      setMensagemConclusao(cfg.conclusionMsg || MENSAGEM_CONCLUSAO_PADRAO);
      setModeloGemini(cfg.aiModel || GEMINI_MODELO_PADRAO);
      setTema(temaValido(cfg.theme));
      // A senha vem cifrada do disco e só é aberta aqui, em memória.
      //
      // Config vindo do Orion 1.x tem a senha em TEXTO PLANO neste campo. O
      // cofre não consegue decifrar aquilo e devolve '' — resultado: quem
      // atualizar da 1.x digita a senha uma vez a mais, e a partir daí ela
      // passa a ser gravada cifrada, substituindo o texto plano no arquivo.
      // Ler o valor legado seria mais cômodo e manteria a senha em claro
      // circulando; não vale a troca.
      const senhaEmClaro = cfg.savedPassword ? await segredo.ler(cfg.savedPassword) : '';
      if (!vivo) return;

      setCredenciais({
        lembrar: !!cfg.rememberCredentials,
        email: cfg.savedEmail || '',
        senha: senhaEmClaro,
        senhaCifrada: senhaEmClaro ? cfg.savedPassword : '',
      });

      // A chave guardada no login anterior. Precisa valer ANTES de qualquer
      // consulta: aplicarSessao já lê orion.operador para descobrir o código.
      if (cfg.supabaseUrl && cfg.supabaseChave) {
        configurarSupabase({
          url: cfg.supabaseUrl, chave: cfg.supabaseChave, origem: 'sessao',
        });
      }

      // Sessão persistida: entra direto, sem passar pela tela de login.
      if (cfg.operatorName && cfg.operatorEmail) {
        await aplicarSessao(cfg.operatorName, cfg.operatorEmail);
      } else if (cfg.rememberCredentials && cfg.savedEmail && senhaEmClaro) {
        // Credenciais guardadas: tenta o login sozinho. Aqui a chave vem
        // fresca do n8n, então uma rotação é pega já nesta entrada.
        const res = await autenticar(cfg.savedEmail, senhaEmClaro);
        if (res.ok && vivo) {
          if (res.supabase) configurarSupabase(res.supabase);
          const sessao = await aplicarSessao(res.nome, res.email);
          registrarAcesso('login', sessao);
        }
      }

      if (vivo) setCarregando(false);
    };

    if (ehDesktop) {
      const cancelar = configPlataforma.aoCarregar(aplicarConfig);
      // Se o processo principal não responder (raro, mas trava a tela quando
      // acontece), libera a interface e cai no login manual.
      const tempoLimite = setTimeout(() => {
        if (vivo && !configRecebida.current) setCarregando(false);
      }, 4000);
      return () => { vivo = false; clearTimeout(tempoLimite); cancelar(); };
    }

    aplicarConfig(configPlataforma.ler() || {});
    return () => { vivo = false; };
  }, [aplicarSessao]);

  // ── Versão e atualização ──────────────────────────────────────────────────

  useEffect(() => {
    let vivo = true;
    versaoApp().then(v => { if (vivo) setVersao(v); });
    const cancelar = atualizacao.aoFicarPronta(() => setUpdatePronto(true));
    const cancelarEstado = atualizacao.aoMudarEstado(e => { if (vivo) setEstadoUpdate(e); });
    // A busca da inicialização pode ter corrido antes desta tela escutar.
    atualizacao.estado().then(e => {
      if (!vivo || !e) return;
      setEstadoUpdate(e);
      if (e.fase === 'pronto') setUpdatePronto(true);
    });
    return () => { vivo = false; cancelar(); cancelarEstado(); };
  }, []);

  /** Clique em "Verificar atualizações": busca na hora e conta o resultado num aviso. */
  const verificarUpdate = useCallback(async () => {
    const e = await atualizacao.verificar();
    if (!e) return;
    setEstadoUpdate(e);
    if (e.fase === 'atualizado') {
      avisar(`Você já está na versão mais recente (v${versao}).`, { tipo: 'ok' });
    } else if (e.fase === 'baixando') {
      avisar(`Versão ${e.versao || 'nova'} encontrada — baixando. O botão fica verde quando estiver pronta.`, { tipo: 'info' });
    } else if (e.fase === 'pronto') {
      avisar(`A versão ${e.versao || 'nova'} já está baixada. Clique no botão verde para instalar.`, { tipo: 'info' });
    } else if (e.fase === 'erro') {
      avisar('Não foi possível verificar atualizações agora. Tente de novo mais tarde.', { tipo: 'erro', titulo: 'Atualização' });
    } else if (e.fase === 'indisponivel') {
      avisar('Atualizações só funcionam no Orion instalado.', { tipo: 'info' });
    }
  }, [avisar, versao]);

  // ── Modo da janela ────────────────────────────────────────────────────────
  // Quem manda é o processo principal: ele muda os bounds e avisa aqui. A
  // classe no <body> acompanha, para o fundo opaco valer só no modo expandido
  // (no modo botão a janela é transparente e só o disco aparece).
  useEffect(() => {
    if (!ehDesktop) return undefined;
    const cancelarModo = janela.aoMudarModo(modo => setModoJanela(modo));
    const cancelarMax = janela.aoMudarMaximizado(cheia => setMaximizado(!!cheia));
    return () => { cancelarModo(); cancelarMax(); };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('app-expandido', modoJanela === 'expanded');
  }, [modoJanela]);

  // ── Cadastro do operador, acompanhado ─────────────────────────────────────
  //
  // O setor era lido uma vez, no login, e congelava: promover alguém de SIS
  // para SUP no banco não mudava nada na tela até ele sair e entrar de novo —
  // e ninguém adivinha que precisa fazer isso.
  //
  // `aplicarPerfil` é o ponto único de entrada da atualização. Ele compara
  // antes de gravar: um UPDATE em qualquer coluna da linha (jornada, telefone)
  // dispara o realtime, e trocar o objeto `operador` à toa recarregaria filas,
  // atividade e a navegação inteira sem motivo.
  const aplicarPerfil = useCallback((perfil) => {
    if (!perfil) return;
    setOperador((atual) => {
      if (!atual) return atual;
      const setor = String(perfil.setor || '');
      const codigo = perfil.codigo ?? atual.codigo;
      const admin = perfil.admin === true;
      if (setor === String(atual.setor || '')
        && codigo === atual.codigo
        && admin === (atual.admin === true)) return atual;

      // Ganhou ou perdeu o painel no meio da sessão. Avisa pelo mesmo motivo do
      // aviso de setor, logo abaixo: um item que aparece ou some sozinho da
      // barra lateral, sem explicação, parece defeito.
      if (admin !== (atual.admin === true)) {
        avisar(
          admin
            ? 'Seu cadastro agora é de gerência: o painel do suporte foi liberado.'
            : 'Seu cadastro saiu da gerência: o painel do suporte foi recolhido.',
          { tipo: 'info', titulo: 'Perfil atualizado', duracao: 12000 },
        );
      }

      // Mudou de perfil no meio da sessão: a navegação vai mudar embaixo dele,
      // e um menu que troca sozinho sem explicação parece defeito.
      if (ehSetorSuporte(setor) !== ehSetorSuporte(atual.setor)) {
        avisar(
          ehSetorSuporte(setor)
            ? 'Seu cadastro agora é do Suporte: as telas de atendimento foram liberadas.'
            : 'Seu cadastro saiu do Suporte: as telas de atendimento foram recolhidas.',
          { tipo: 'info', titulo: 'Perfil atualizado', duracao: 12000 },
        );
      }
      return { ...atual, setor, codigo, admin };
    });
  }, [avisar]);

  /** Relê o cadastro agora. Exposta para o botão das Configurações. */
  const revalidarPerfil = useCallback(async () => {
    const email = operador?.email;
    if (!email) return;
    aplicarPerfil(await carregarPerfil(email));
  }, [operador, aplicarPerfil]);

  useEffect(() => {
    const email = operador?.email;
    if (!email) return undefined;

    // 1) Realtime: o caminho normal para mudanças em orion.operador. Ele avisa
    //    QUE mudou, não o que mudou — a leitura vem sempre da view, porque o
    //    setor mora em space.usuarios. Ver `assinarOperador`.
    const pararRealtime = assinarOperador(email, revalidarPerfil);

    // 2) Relógio: cobre realtime desligado na publicação da tabela, conexão
    //    caída e UPDATE feito por fora (script, restore).
    const id = setInterval(revalidarPerfil, INTERVALO_PERFIL_MS);

    // 3) Foco: o gesto de quem pediu "muda meu acesso aí" e volta para conferir.
    const aoVoltar = () => { if (!document.hidden) revalidarPerfil(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    const pararRestauracao = ehDesktop ? janela.aoRestaurar(revalidarPerfil) : null;

    return () => {
      pararRealtime();
      clearInterval(id);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
      if (typeof pararRestauracao === 'function') pararRestauracao();
    };
  }, [operador, aplicarPerfil, revalidarPerfil]);

  // ── Permissões ────────────────────────────────────────────────────────────
  //
  // O que este operador enxerga, conforme o setor. Uma referência estável por
  // setor: sem o memo, cada render devolveria um objeto novo e tudo que depende
  // dele (navegação, monitor de atividade) recalcularia à toa.
  const permissoes = useMemo(
    () => permissoesDoSetor(operador?.setor, operador?.admin),
    [operador?.setor, operador?.admin],
  );

  // Trocar de tela passa por aqui: uma view que este operador não pode abrir
  // cai na inicial dele. É a rede de segurança do bloqueio — a Sidebar já não
  // mostra o item, mas o app navega por outros caminhos também (atalhos, o
  // "registrar incidente" do pós-envio, sessão restaurada do disco).
  const irParaView = useCallback((chave) => {
    setView(permissoes[chave] === false ? viewInicial(permissoes) : chave);
  }, [permissoes]);

  // Sessão restaurada do disco pode cair numa tela proibida — e o setor só
  // chega depois do login. Este efeito corrige assim que ele chega.
  useEffect(() => {
    if (!operador) return;
    if (permissoes[view] === false) setView(viewInicial(permissoes));
  }, [operador, permissoes, view]);

  // ── Controle de atividade ─────────────────────────────────────────────────
  // Ativo/Inativo do operador, com monitor de ociosidade. Só vale para o
  // SUPORTE e para quem tem jornada cadastrada em orion.operador — ver
  // services/useAtividade.js. Fora do suporte não há o que medir: a régua é o
  // atendimento de ticket.
  const atividade = useAtividade({
    operador,
    avisar,
    habilitado: permissoes.suporte,
  });

  // ── Fila de retorno ───────────────────────────────────────────────────────
  //
  // Vive aqui, e não na tela da Formalização, pelo mesmo motivo do controle de
  // atividade: o compromisso tem hora marcada e o operador pode estar em
  // qualquer aba quando ela chegar. Montado na tela, o aviso desapareceria
  // justamente quando ele fosse consultar um incidente.
  // Só o suporte tem fila de retorno: ela é dos tickets de helpdesk.
  const filaRetorno = useFilaRetorno({
    operador: permissoes.suporte ? operador : null,
    avisar,
  });

  // ── Retornos do setor de Sistemas ─────────────────────────────────────────
  //
  // Os chamados deste operador cuja correção ficou pronta e voltou para ele. O
  // ticket em si chega pela fila de retorno acima (status 5 do MySuite); isto
  // aqui é o aviso de que não é um agendamento comum — há uma atualização
  // esperando para ser aplicada no cliente.
  //
  // Vive no contexto pelo mesmo motivo das filas: o retorno pode chegar
  // enquanto o operador está em qualquer aba, ou com o app recolhido no botão
  // flutuante, e a notificação precisa sair de todo jeito.
  const retornoSistemas = useRetornoSistemas(
    permissoes.suporte ? operador?.email : null,
    operador?.codigo ?? null,
  );

  // ── Filas do MySuite ──────────────────────────────────────────────────────
  //
  // "Aguardando solução", "Em atendimento" e "Setor de sistemas". Sobem para cá
  // pelo mesmo motivo da fila de retorno: o relógio precisa girar mesmo com a
  // Formalização fechada ou o app recolhido no botão flutuante. Antes ele vivia
  // dentro da tela e parava junto com ela — ver services/useFilasMysuite.js.
  const filasMysuite = useFilasMysuite({ operador: permissoes.suporte ? operador : null });

  // ── Notificações (sininho) ────────────────────────────────────────────────
  //
  // A caixa de entrada do operador logado — ver services/useNotificacoes.js.
  // Clicar numa notificação marca como lida e abre o resumo do ticket por
  // cima de qualquer tela (o shell desenha o resumo quando há número aqui).
  const [ticketDaNotificacao, setTicketDaNotificacao] = useState(null);
  const abrirNotificacaoRef = useRef(null);
  const notificacoes = useNotificacoes(operador?.codigo, {
    aoChegar: (n) => {
      avisar(n.texto || n.titulo, {
        tipo: 'info',
        titulo: n.texto ? n.titulo : 'Nova notificação',
        duracao: 9000,
        aoClicar: () => abrirNotificacaoRef.current && abrirNotificacaoRef.current(n),
      });
      // O retorno do setor de Sistemas já foi para a bandeja quando foi
      // detectado (useRetornoSistemas) — aqui seria o mesmo aviso duas vezes.
      if (n.tipo !== 'retorno_sistemas') {
        notificar(n.titulo, n.texto || '', { tag: `orion-notificacao-${n.id}` });
      }
    },
  });
  const { marcarLida: marcarNotificacaoLida } = notificacoes;
  const abrirNotificacao = useCallback((n) => {
    if (!n) return;
    if (!n.lida_em) marcarNotificacaoLida(n.id);
    if (n.numero_ticket) setTicketDaNotificacao(String(n.numero_ticket));
  }, [marcarNotificacaoLida]);
  abrirNotificacaoRef.current = abrirNotificacao;

  // ── Realtime de incidentes ────────────────────────────────────────────────
  // Ligado no login e não ao abrir a aba: o operador precisa saber de mudança
  // de status mesmo trabalhando em outra tela.

  useEffect(() => {
    if (!operador) return undefined;
    return assinarIncidentes({
      aoAtualizar: (novo, antigo) => {
        if (novo?.status && antigo?.status && novo.status !== antigo.status) {
          avisar(`Incidente do ticket ${novo.numero_ticket} mudou para "${novo.status}".`,
            { tipo: 'info', titulo: 'Incidente atualizado', duracao: 9000 });
        }
      },
    });
  }, [operador, avisar]);

  // ── Logout ao fechar ──────────────────────────────────────────────────────

  useEffect(() => {
    const aoSair = () => registrarLogoutNaSaida(operador);
    window.addEventListener('beforeunload', aoSair);
    return () => window.removeEventListener('beforeunload', aoSair);
  }, [operador]);

  const valor = useMemo(() => ({
    // sessão
    operador, carregando, erroLogin, autenticando, entrar, sair,
    // preferências
    tema, trocarTema, mensagemConclusao, setMensagemConclusao,
    modeloGemini, setModeloGemini, credenciais, setCredenciais, salvarConfig,
    // permissões por setor (ver services/permissoes.js)
    permissoes,
    // relê o cadastro no banco agora, sem esperar o relógio nem o realtime
    revalidarPerfil,
    // navegação — `setView` passa pela checagem de permissão de propósito:
    // é o único ponto por onde a troca de tela acontece.
    view, setView: irParaView, sidebarRecolhida, setSidebarRecolhida,
    ticketParaIncidente, setTicketParaIncidente,
    textoParaCopiar, setTextoParaCopiar,
    /**
     * Abre o registro de incidente.
     *
     * Aceita só o número (quem clica em "+ Novo" na aba Incidentes) ou o pacote
     * inteiro vindo de um atendimento — com os campos que a IA extraiu, os
     * prints já convertidos, o status inicial e o GUT. É o que evita reescrever
     * o atendimento inteiro no formulário.
     */
    abrirRegistroIncidente: (dados) => {
      setTicketParaIncidente(typeof dados === 'object' && dados !== null
        ? dados
        : { numeroTicket: String(dados) });
      irParaView('incidentes');
    },
    formalizacaoParaReusar, setFormalizacaoParaReusar,

    /**
     * Leva um atendimento do histórico para a Formalização.
     *
     * O que viaja é o TEXTO — anotações, ticket formalizado, assunto e
     * classificação. O número do ticket vai junto só para a tela saber de onde
     * o texto veio: reutilizar é aproveitar o que já foi escrito em OUTRO
     * chamado, e o novo número é pedido na fase 1 (ver Helpdesk.js).
     */
    reutilizarFormalizacao: (entrada) => { setFormalizacaoParaReusar(entrada); irParaView('helpdesk'); },

    /**
     * Assume o ticket no MySuite — o primeiro passo de "Atender".
     *
     * ESPERA a resposta e devolve `true` só quando o ticket passou a estar em
     * atendimento com quem clicou. Quem chama usa isso para decidir se abre a
     * formalização: **preencher um ticket que não está em atendimento não faz
     * sentido** — o texto sairia em nome de um atendimento que não é seu.
     *
     * Mora aqui, e não em cada tela, porque os dois lugares que atendem um
     * ticket precisam do MESMO comportamento: os cards das filas, na
     * Formalização, e o aviso da fila de retorno, que vive no shell. Duplicar
     * a chamada duplicaria também as mensagens — e um dia elas divergiriam.
     *
     * `dica` é o registro do ticket que a tela já tem (o item da lista): a fila
     * de pendentes não conhece tickets no Setor de Sistemas, e sem ele o clique
     * naquele cartão seria sempre recusado.
     *
     * Nunca lança: toda recusa vira aviso na tela e `false` na resposta.
     *
     * PULO DE FILA E MESMO CLIENTE (ver services/puloFila.js). Antes de
     * assumir, a fila de pendentes é lida de novo: se havia ticket livre À
     * FRENTE deste, o analista só segue justificando — e o pulo é gravado para
     * a gerência. Depois de assumir, se há outros livres do mesmo cliente, o
     * Orion oferece puxar todos. A análise só falha para o lado de deixar
     * atender: com o MySuite oscilando, nada é perguntado.
     */
    assumirAoAtender: async (numero, dica = null) => {
      const analise = operador?.codigo
        ? await analisarColeta(numero, dica).catch(() => null)
        : null;

      let motivo = '';
      if (analise?.pulados.length) {
        const resposta = await perguntarColeta({
          tipo: 'pulo', numero, posicao: analise.posicao, pulados: analise.pulados,
        });
        if (!resposta?.motivo) return false;
        motivo = resposta.motivo;
      }

      const r = await assumirTicketAoAtender(numero, operador?.codigo, dica);

      // Sem POST e mesmo assim liberado: o ticket já estava em atendimento com
      // ele. É o clique repetido no mesmo card, enquanto a fila ainda mostra o
      // ticket no lugar antigo.
      if (!r.assumiu && liberaFormalizacao(r.motivo)) return true;

      if (r.assumiu) {
        // A confirmação só sai depois da carga do dia no MySuite (ver
        // `confirmouEmAtendimento`), então a tabela que as filas leem já tem o
        // status novo: recarregar agora tira o ticket do cartão de origem em
        // vez de esperar o relógio de 5 minutos. Sem await — a formalização
        // abre enquanto os cartões atualizam.
        filasMysuite.recarregar();
        filaRetorno.recarregar();
        avisar(`Ticket ${numero} agora está em atendimento com você.`, { tipo: 'ok' });

        // O pulo só é gravado depois de o ticket ser de fato assumido: pulo que
        // não aconteceu não é apurado. Se a gravação falhar, o analista fica
        // sabendo — o ticket já é dele, e o registro é o que faltou.
        if (motivo) {
          registrarPulo({ operador, analise, motivo }).catch((err) => {
            console.warn('[Orion] Registro do pulo de fila:', err.message);
            avisar(
              `O ticket ${numero} ficou com você, mas o registro do pulo de fila não foi gravado `
              + `(${err.message}). Avise a gerência com a sua justificativa.`,
              { tipo: 'erro', titulo: 'Pulo de fila não registrado', duracao: 15000 },
            );
          });
        }

        // Os outros do mesmo cliente: sem await, para quem chamou seguir (o
        // resumo atualiza, a formalização abre) enquanto o diálogo pergunta.
        if (analise?.mesmoCliente.length) {
          (async () => {
            const resposta = await perguntarColeta({
              tipo: 'mesmoCliente', numero, tickets: analise.mesmoCliente,
              cliente: analise.cliente, nomeCliente: analise.nomeCliente,
            });
            const escolhidos = resposta?.selecionados || [];

            // O que ficou na fila vai para a gerência com a justificativa —
            // a lista inteira ou só parte dela.
            if (resposta?.ignorados?.length && resposta.motivo) {
              registrarMesmoClienteIgnorado({
                operador, analise, ignorados: resposta.ignorados, motivo: resposta.motivo,
              }).catch((err) => {
                console.warn('[Orion] Registro de mesmo cliente ignorado:', err.message);
                avisar(
                  `A justificativa dos tickets do mesmo cliente não foi gravada (${err.message}). `
                  + 'Avise a gerência.',
                  { tipo: 'erro', titulo: 'Justificativa não registrada', duracao: 15000 },
                );
              });
            }

            if (!escolhidos.length) return;

            const aviso = avisar(`Puxando ${escolhidos.length} ticket(s) do mesmo cliente…`, { duracao: 0 });
            const puxados = [];
            const recusados = [];
            // Um por vez: cada marcação espera a confirmação do MySuite, e
            // disparar todas juntas atropelaria a carga do dia.
            for (const t of escolhidos) {
              // eslint-disable-next-line no-await-in-loop
              const rr = await assumirTicketAoAtender(t.codigo, operador?.codigo, t);
              if (rr.assumiu || liberaFormalizacao(rr.motivo)) puxados.push(t.codigo);
              else recusados.push(t.codigo);
            }
            fecharToast(aviso);
            filasMysuite.recarregar();
            filaRetorno.recarregar();

            if (puxados.length) {
              avisar(`Também ficaram com você: ${puxados.map(c => `#${c}`).join(', ')}.`, { tipo: 'ok', duracao: 8000 });
            }
            if (recusados.length) {
              avisar(
                `Não deu para puxar ${recusados.map(c => `#${c}`).join(', ')} — outro colega pode ter `
                + 'pegado antes, ou o MySuite não confirmou. Confira na tela de Filas.',
                { tipo: 'erro', titulo: 'Alguns tickets não vieram', duracao: 12000 },
              );
            }
          })();
        }
        return true;
      }

      // Cada recusa diz o que fazer a seguir. Um "não deu" sem motivo deixaria
      // o operador clicando no mesmo botão.
      const deOutro = [
        `O ticket ${numero} está em atendimento com `
        + `${r.nomeDono || `o operador ${r.dono}`}, não com você. Fale com quem está `
        + 'atendendo antes de assumir — o Orion não tira ticket de ninguém.',
        'Ticket de outro operador',
      ];

      const recusa = {
        'ja-de-outro': deOutro,
        'de-outro': [
          `O ticket ${numero} é do operador ${r.nomeDono || r.dono}, não seu. `
          + 'Fale com ele antes de atender — o Orion não tira ticket de ninguém.',
          'Ticket de outro operador',
        ],
        'sem-operador': [
          'Seu e-mail não está cadastrado em orion.operador. Sem o código do '
          + 'operador o Orion não tem em nome de quem marcar o ticket.',
          'Sem cadastro de operador',
        ],
        desconhecido: [
          `Não deu para confirmar a situação do ticket ${numero} no MySuite — `
          + 'ele não está na fila de pendentes. Confira o ticket antes de atender.',
          'Ticket fora da fila',
        ],
        'nao-gravou': [
          `O MySuite aceitou o pedido, mas o ticket ${numero} NÃO ficou em `
          + 'atendimento com você. Tente de novo; se repetir, marque pelo MySuite '
          + 'e avise o time.',
          'A marcação não pegou',
        ],
        falha: [
          `O MySuite não respondeu ao marcar o ticket ${numero}. Ele NÃO ficou em `
          + 'atendimento com você; tente de novo em instantes.',
          'MySuite fora do ar',
        ],
      }[r.motivo];

      if (recusa) avisar(recusa[0], { tipo: 'erro', titulo: recusa[1], duracao: 12000 });
      return false;
    },

    /** Leva à Formalização já buscando o ticket. */
    abrirTicketNaFormalizacao: (numero) => {
      if (numero == null) return;
      setTicketParaAbrir(String(numero));
      irParaView('helpdesk');
    },
    ticketParaAbrir,
    setTicketParaAbrir,

    /** Leva à aba Incidentes já abrindo o incidente daquele ticket. */
    abrirIncidenteDoTicket: (numero) => {
      if (numero == null) return;
      setIncidenteParaAbrir(String(numero));
      irParaView('incidentes');
    },
    incidenteParaAbrir,
    setIncidenteParaAbrir,
    // avisos
    toasts, avisar, fecharToast,
    // app
    versao, updatePronto, instalarUpdate: atualizacao.instalar,
    estadoUpdate, verificarUpdate,
    ehDesktop, janela,
    // estado da janela (desktop)
    modoJanela, maximizado,
    // controle de atividade
    atividade,
    // tickets com retorno agendado (status 5)
    filaRetorno,
    // filas do operador no MySuite (status 1, 36 e 18), atualizadas de 5 em 5 min
    filasMysuite,
    // chamados deste operador que voltaram do setor de Sistemas com a correção
    retornoSistemas,
    // diálogo em aberto da coleta (pulo de fila / mesmo cliente) — ver DialogoColeta
    dialogoColeta, responderColeta,
    // caixa de entrada do sininho, e o ticket aberto a partir dela
    notificacoes, abrirNotificacao, ticketDaNotificacao, setTicketDaNotificacao,
  }), [
    operador, carregando, erroLogin, autenticando, entrar, sair,
    tema, trocarTema, mensagemConclusao, modeloGemini, credenciais, salvarConfig,
    view, sidebarRecolhida, ticketParaIncidente, textoParaCopiar, formalizacaoParaReusar, ticketParaAbrir, incidenteParaAbrir, toasts, avisar, fecharToast, versao, updatePronto,
    estadoUpdate, verificarUpdate,
    modoJanela, maximizado, atividade, filaRetorno, filasMysuite, retornoSistemas, permissoes, irParaView, revalidarPerfil,
    dialogoColeta, responderColeta, perguntarColeta,
    notificacoes, abrirNotificacao, ticketDaNotificacao,
  ]);

  return <OrionContext.Provider value={valor}>{children}</OrionContext.Provider>;
}
